/** One dependency-free implementation shared by SSR, browser and compatibility readers. */
export const deriveOfferAvailability = active => active ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
// Retained arithmetic API for existing callers/tests. An arithmetic horizon is
// NOT evidence of a listing's expiry and is never used by an offer constructor.
export function offerPriceValidUntil(from=new Date()) {
  const date=new Date(from.getTime());date.setFullYear(date.getFullYear()+1);
  return date.toISOString().slice(0,10);
}
export function verifiedOfferExpiry(value) {
  if(typeof value!=="string"||!/^\d{4}-\d{2}-\d{2}$/.test(value))return undefined;
  const date=new Date(value+"T00:00:00Z");
  return Number.isFinite(date.getTime())&&date.toISOString().slice(0,10)===value?value:undefined;
}
export function fuelTypeLabel(value) {
  const number=typeof value==="number"||typeof value==="string"&&/^\d+$/.test(value)?Number(value):NaN;
  return Number.isInteger(number)?({1:"Petrol",2:"Diesel",3:"Electric",4:"Hybrid",5:"LPG",6:"CNG",7:"Hydrogen"})[number]||null:null;
}
export function normalizeOfferPrice(value) {
  if(typeof value==="number")return Number.isFinite(value)&&value>0?value:null;
  if(typeof value!=="string"||!/^\d+(?:\.\d+)?$/.test(value.trim()))return null;
  const number=Number(value);return Number.isFinite(number)&&number>0?number:null;
}
export function normalizeOfferCurrency(value) {
  if(typeof value!=="string")return null;
  const code=value.trim().toUpperCase();return /^[A-Z]{3}$/.test(code)?code:null;
}
function singleOffer(entry,context) {
  const rate={"@type":"UnitPriceSpecification",price:entry.price,priceCurrency:context.currency,
    unitCode:entry.unitCode,referenceQuantity:{"@type":"QuantitativeValue",value:1,unitCode:entry.unitCode}};
  return {"@type":"Offer",...(context.id?{"@id":context.id}:{}),price:entry.price,
    priceCurrency:context.currency,availability:context.availability,url:context.url,
    ...(context.expiry?{priceValidUntil:context.expiry}:{}),
    priceSpecification:entry.minimum?[rate,{"@type":"PriceSpecification",
      name:"Minimum rental charge (not an additional fee)",price:entry.minimum,priceCurrency:context.currency}]:rate};
}
export function buildCarOfferNode(input) {
  const currency=normalizeOfferCurrency(input.currencyCode);
  if(!currency||input.publicUnavailable===true)return null;
  const context={currency,url:input.url,availability:deriveOfferAvailability(
    input.rentalDrivingActive||input.rentalChauffeuredActive||input.rentalEventActive),
    expiry:verifiedOfferExpiry(input.priceValidUntil)};
  // Existing authenticated wizard-only preview behavior; no public reader sets it.
  if(input.preview)return singleOffer({price:0,unitCode:"DAY"},{...context,id:input.offerId});
  const entries=[];
  const driving=normalizeOfferPrice(input.priceDriving),chauffeur=normalizeOfferPrice(input.chauffeuredHourlyRate),
    eventHour=normalizeOfferPrice(input.eventHourlyRate),eventDay=normalizeOfferPrice(input.priceEvent);
  if(input.rentalDrivingActive&&driving!=null)entries.push({price:driving,unitCode:"DAY"});
  if(input.rentalChauffeuredActive&&chauffeur!=null)entries.push({price:chauffeur,unitCode:"HUR",
    minimum:normalizeOfferPrice(input.chauffeurMinimum)??normalizeOfferPrice(input.minimumPrice)});
  if(input.rentalEventActive&&(eventHour!=null||eventDay!=null))entries.push({
    price:eventHour??eventDay,unitCode:eventHour!=null?"HUR":"DAY",
    minimum:normalizeOfferPrice(input.eventMinimum)??normalizeOfferPrice(input.minimumPrice)});
  if(!entries.length)return null;
  if(entries.length===1)return singleOffer(entries[0],{...context,id:input.offerId});
  const prices=entries.map(entry=>entry.price);
  return {"@type":"AggregateOffer","@id":input.offerId,priceCurrency:currency,
    lowPrice:Math.min(...prices),highPrice:Math.max(...prices),offerCount:entries.length,
    availability:context.availability,url:input.url,offers:entries.map(entry=>singleOffer(entry,context))};
}
export function validReviewAggregate(rating,count) {
  return typeof rating==="number"&&Number.isFinite(rating)&&rating>=1&&rating<=5&&
    typeof count==="number"&&Number.isSafeInteger(count)&&count>0;
}
export function isLandingRichResultEvidence(value) {
  if(!value||typeof value!=="object"||value.eligible!==true||"aggregateRating" in value)return false;
  // Keep the stricter existing browser/source gate, never lower it to the old SSR helper's six.
  if(!Array.isArray(value.cars)||value.cars.length<8||value.cars.length>12)return false;
  if(!Array.isArray(value.faqs)||value.faqs.length<4||value.faqs.length>5||
    !value.faqs.every(faq=>faq&&typeof faq.q==="string"&&faq.q.trim()&&typeof faq.a==="string"&&faq.a.trim()))return false;
  if(!["pageScope","serviceType","areaServed","summary"].every(key=>typeof value[key]==="string"&&value[key].trim()))return false;
  if(value.fallback!==undefined&&(!value.fallback||value.fallback.sameMarket!==true||
    typeof value.fallback.label!=="string"||!value.fallback.label.trim()))return false;
  if(!Array.isArray(value.details)||value.details.length!==value.cars.length||
    !value.details.every(detail=>detail&&typeof detail.heading==="string"&&detail.heading.trim()&&
      typeof detail.body==="string"&&detail.body.trim()))return false;
  if(!value.cars.every(car=>car&&typeof car.name==="string"&&car.name.trim()&&
    typeof car.url==="string"&&car.url.startsWith("/")&&!car.url.startsWith("//")&&
    typeof car.town==="string"&&car.town.trim()&&Number.isInteger(car.year)&&car.year>1885&&
    typeof car.price==="number"&&normalizeOfferPrice(car.price)!=null&&
    (car.priceUnit===undefined||["day","hour","minimum"].includes(car.priceUnit))&&
    typeof car.currency==="string"&&/^[A-Z]{3}$/.test(car.currency)&&
    car.image&&typeof car.image.url==="string"&&car.image.url&&
    Number.isFinite(car.image.width)&&car.image.width>0&&Number.isFinite(car.image.height)&&car.image.height>0&&
    typeof car.image.alt==="string"&&car.image.alt.trim()))return false;
  if(new Set(value.cars.map(car=>car.currency)).size!==1)return false;
  return value.cars.every(car=>car.aggregateRating===undefined||
    car.aggregateRating&&validReviewAggregate(car.aggregateRating.ratingValue,car.aggregateRating.reviewCount));
}
export function buildLandingRichResultNodes(evidence,siteUrl,pageUrl) {
  if(!isLandingRichResultEvidence(evidence))return null;
  const prices=evidence.cars.map(car=>car.price);
  return {aggregateOffer:{"@type":"AggregateOffer",lowPrice:Math.min(...prices),highPrice:Math.max(...prices),
    priceCurrency:evidence.cars[0].currency,offerCount:evidence.cars.length,url:pageUrl},
    itemList:{"@type":"ItemList",numberOfItems:evidence.cars.length,
      itemListElement:evidence.cars.map((car,index)=>({"@type":"ListItem",position:index+1,
        url:new URL(car.url,siteUrl).href,name:car.name,item:{"@type":"Product",name:car.name,url:new URL(car.url,siteUrl).href,
          image:{"@type":"ImageObject",contentUrl:car.image.url,width:car.image.width,height:car.image.height,caption:car.image.alt},
          offers:{"@type":"Offer",price:car.price,priceCurrency:car.currency,url:new URL(car.url,siteUrl).href,
            availability:"https://schema.org/InStock",...(car.priceUnit?{
              priceSpecification:{"@type":"UnitPriceSpecification",price:car.price,priceCurrency:car.currency,
                unitText:car.priceUnit==="hour"?"HOUR":car.priceUnit==="day"?"DAY":"BOOKING_MINIMUM"}}:{})},
          ...(car.aggregateRating?{aggregateRating:{"@type":"AggregateRating",...car.aggregateRating}}:{})}}))}};
}
/** Normalize graph ownership/serialization, never string-rewrite canonical URLs. */
export function publicJsonLdGraph(value,{canonical,title,description,language}={}) {
  const nodes=[];
  const append=node=>{
    if(Array.isArray(node)){node.forEach(append);return;}
    if(!node||typeof node!=="object")throw Error("Invalid public structured-data node");
    if(Array.isArray(node["@graph"])){node["@graph"].forEach(append);return;}
    let {"@context":context,...record}=node;
    // A WebPage describes this resolved document, not the reusable template's
    // authoring path. Other entities (organization, products, profiles) retain
    // their own source identities; this is not blanket URL string replacement.
    if(canonical&&[].concat(record["@type"]||[]).includes("WebPage")) {
      const hash=record["@id"]?new URL(record["@id"],canonical).hash:"";
      record={...record,...(record["@id"]?{"@id":canonical+hash}:{}),url:canonical};
    }
    const existing=record["@id"]&&nodes.find(item=>item["@id"]===record["@id"]);
    if(existing){if(JSON.stringify(existing)!==JSON.stringify(record))throw Error("Conflicting structured-data identity");return;}
    nodes.push(record);
  };
  if(value)append(value);
  if(!nodes.length&&canonical&&title&&language)append({"@type":"WebPage",url:canonical,name:title,
    ...(description?{description}:{}),inLanguage:language});
  if(!nodes.length)return undefined;
  return {"@context":"https://schema.org","@graph":nodes};
}
